import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/Card'
import { ArrowLeft, FolderOpen, Plus, X, Loader2 } from 'lucide-react'
import { useToast } from '@/contexts/ToastContext'
import { projectsApi, Shipyard, ComplianceRuleProfile } from '@/lib/projectsApi'
import { ComplianceProfileSelector } from '@/components/projects/ComplianceProfileSelector'
import { useTranslation } from 'react-i18next'

export const CreateProjectPage: React.FC = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { success, error: showError } = useToast()

  const [shipyards, setShipyards] = useState<Shipyard[]>([])
  const [complianceProfiles, setComplianceProfiles] = useState<ComplianceRuleProfile[]>([])
  const [saving, setSaving] = useState(false)

  // add-shipyard inline form
  const [showAddShipyard,    setShowAddShipyard]    = useState(false)
  const [newShipyardName,    setNewShipyardName]    = useState('')
  const [newShipyardCode,    setNewShipyardCode]    = useState('')
  const [savingShipyard,     setSavingShipyard]     = useState(false)

  // form fields
  const [shipyardId,   setShipyardId]   = useState<number | ''>('')
  const [hullNumber,   setHullNumber]   = useState('')
  const [imoNumber,    setImoNumber]    = useState('')
  const [projectName,  setProjectName]  = useState('')
  const [vesselName,   setVesselName]   = useState('')
  const [vesselType,   setVesselType]   = useState('')
  const [shipOwner,    setShipOwner]    = useState('')
  const [selectedProfileCodes, setSelectedProfileCodes] = useState<string[]>([])

  useEffect(() => {
    Promise.all([projectsApi.listShipyards(), projectsApi.listComplianceProfiles()])
      .then(([sy, profiles]) => {
        setShipyards(sy)
        setComplianceProfiles(profiles)
      })
      .catch((err) => showError(err.message ?? t('projectCreate.loadFailed')))
  }, [showError, t])

  const handleAddShipyard = async () => {
    if (!newShipyardName.trim()) { showError(t('projectCreate.shipyardNameRequired')); return }
    setSavingShipyard(true)
    try {
      const created = await projectsApi.createShipyard({
        name: newShipyardName.trim(),
        code: newShipyardCode.trim() || undefined,
      })
      setShipyards((prev) => [...prev, created].sort((a, b) => a.name.localeCompare(b.name)))
      setShipyardId(created.id)
      setShowAddShipyard(false)
      setNewShipyardName('')
      setNewShipyardCode('')
      success(t('projectCreate.shipyardAdded', { name: created.name }))
    } catch (err: any) {
      showError(err.message ?? t('projectCreate.shipyardCreateFailed'))
    } finally {
      setSavingShipyard(false)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!hullNumber.trim()) { showError(t('projectCreate.hullRequired')); return }
    if (selectedProfileCodes.length < 1 || selectedProfileCodes.length > 2) {
      showError(t('projectCreate.selectProfiles'))
      return
    }
    setSaving(true)
    try {
      const project = await projectsApi.createProject({
        shipyard_id:    shipyardId !== '' ? Number(shipyardId) : null,
        project_name:   projectName  || null,
        hull_number:    hullNumber.trim(),
        imo_number:     imoNumber    || null,
        vessel_name:    vesselName   || null,
        vessel_type:    vesselType   || null,
        ship_owner:     shipOwner    || null,
        status:         'active',
        compliance_profiles: selectedProfileCodes.map(profile_code => ({ profile_code })),
      })
      success(t('projectCreate.created'))
      navigate(`/projects/${project.id}`)
    } catch (err: any) {
      showError(err.message ?? t('projectCreate.createFailed'))
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="min-h-screen bg-white dark:bg-slate-900 text-slate-900 dark:text-white">
      {/* Header */}
      <div className="border-b border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800">
        <div className="px-4 sm:px-6 py-4 flex items-center gap-4">
          <Button variant="secondary" size="sm" onClick={() => navigate('/projects')}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <h1 className="text-lg sm:text-xl font-semibold flex items-center gap-2">
              <FolderOpen className="h-5 w-5 text-blue-600 dark:text-blue-400" />
              {t('projectCreate.title')}
            </h1>
            <p className="text-xs sm:text-sm text-slate-500 dark:text-slate-400 mt-0.5">
              {t('projectCreate.subtitle')}
            </p>
          </div>
        </div>
      </div>

      <div className="px-4 sm:px-6 py-6 max-w-2xl mx-auto">
        <form onSubmit={handleSubmit} className="space-y-6">

          {/* Required fields */}
          <Card className="bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700">
            <CardHeader>
              <CardTitle className="text-base">{t('common.required')}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">

              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="text-sm font-medium">{t('reports.shipyard')}</label>
                  {!showAddShipyard && (
                    <button
                      type="button"
                      onClick={() => setShowAddShipyard(true)}
                      className="flex items-center gap-1 text-xs text-blue-600 dark:text-blue-400 hover:underline"
                    >
                      <Plus className="h-3 w-3" />
                      {t('projectCreate.addNew')}
                    </button>
                  )}
                </div>
                <select
                  value={shipyardId}
                  onChange={(e) => setShipyardId(e.target.value === '' ? '' : Number(e.target.value))}
                  className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-700 text-slate-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">{t('projectCreate.selectShipyard')}</option>
                  {shipyards.map((s) => (
                    <option key={s.id} value={s.id}>{s.name} ({s.code})</option>
                  ))}
                </select>

                {showAddShipyard && (
                  <div className="mt-3 p-3 border border-slate-200 dark:border-slate-600 rounded-md bg-slate-50 dark:bg-slate-700/50 space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-medium text-slate-600 dark:text-slate-300">{t('projectCreate.newShipyard')}</span>
                      <button
                        type="button"
                        onClick={() => { setShowAddShipyard(false); setNewShipyardName(''); setNewShipyardCode('') }}
                        className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-200"
                      >
                        <X className="h-3.5 w-3.5" />
                      </button>
                    </div>
                    <div className="grid grid-cols-3 gap-2">
                      <div className="col-span-2">
                        <Input
                          placeholder={t('projectCreate.shipyardNamePlaceholder')}
                          value={newShipyardName}
                          onChange={(e) => setNewShipyardName(e.target.value)}
                          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAddShipyard() } }}
                        />
                      </div>
                      <div>
                        <Input
                          placeholder={t('projectCreate.codePlaceholder')}
                          value={newShipyardCode}
                          onChange={(e) => setNewShipyardCode(e.target.value.toUpperCase())}
                          maxLength={8}
                          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAddShipyard() } }}
                        />
                      </div>
                    </div>
                    <div className="flex justify-end">
                      <Button
                        type="button"
                        size="sm"
                        onClick={handleAddShipyard}
                        disabled={savingShipyard || !newShipyardName.trim()}
                      >
                        {savingShipyard ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : t('projectCreate.addShipyard')}
                      </Button>
                    </div>
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">
                  {t('reports.hullNumber')} <span className="text-red-500">*</span>
                </label>
                <Input
                  value={hullNumber}
                  onChange={(e) => setHullNumber(e.target.value)}
                  placeholder="e.g. H1234"
                  required
                />
              </div>

            </CardContent>
          </Card>

          {/* Class profiles — required because every downstream decision is
              evaluated against a pinned class + edition, never a generic
              regulation checkbox. */}
          <Card className="bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700">
            <CardHeader>
              <CardTitle className="text-base">{t('projectCreate.classification')}</CardTitle>
              <CardDescription>
                {t('projectCreate.classificationDescription')}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {complianceProfiles.length === 0 ? (
                <p className="text-sm text-slate-500 dark:text-slate-400">{t('projectCreate.loadingProfiles')}</p>
              ) : (
                <ComplianceProfileSelector
                  profiles={complianceProfiles}
                  selectedCodes={selectedProfileCodes}
                  onChange={setSelectedProfileCodes}
                  disabled={saving}
                />
              )}
            </CardContent>
          </Card>

          {/* Optional fields */}
          <Card className="bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700">
            <CardHeader>
              <CardTitle className="text-base">{t('common.optional')}</CardTitle>
              <CardDescription>{t('projectCreate.optionalDescription')}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">

              <div>
                <label className="block text-sm font-medium mb-1">{t('projectCreate.projectName')}</label>
                <Input
                  value={projectName}
                  onChange={(e) => setProjectName(e.target.value)}
                  placeholder="e.g. Hull 1234 Cyber Security"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">{t('projectCreate.imoNumber')}</label>
                <Input
                  value={imoNumber}
                  onChange={(e) => setImoNumber(e.target.value)}
                  placeholder="e.g. IMO 9876543"
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">{t('reports.vesselName')}</label>
                  <Input
                    value={vesselName}
                    onChange={(e) => setVesselName(e.target.value)}
                    placeholder="e.g. MV SeaBreeze"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">{t('projectCreate.vesselType')}</label>
                  <Input
                    value={vesselType}
                    onChange={(e) => setVesselType(e.target.value)}
                    placeholder="e.g. LNG Carrier"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">{t('reports.shipOwner')}</label>
                <Input
                  value={shipOwner}
                  onChange={(e) => setShipOwner(e.target.value)}
                  placeholder="e.g. Example Shipping Co."
                />
              </div>

            </CardContent>
          </Card>

          <div className="flex justify-end gap-3">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate('/projects')}
              disabled={saving}
            >
              {t('common.cancel')}
            </Button>
            <Button
              type="submit"
              disabled={saving || !hullNumber.trim() || selectedProfileCodes.length === 0}
            >
              {saving ? t('projectCreate.creating') : t('projectCreate.create')}
            </Button>
          </div>

        </form>
      </div>
    </div>
  )
}
